import { hasChanged, US_STATE_CODE_TO_NAME, US_STATE_TERRITORY_CODES, updateObject, filterByKeys, includesMatch, deepCopy, getInitials, range, isNil } from '@engineering11/utility';
export { filterByKeys } from '@engineering11/utility';
import * as i0 from '@angular/core';
import { EventEmitter, Directive, Output, HostListener, Input, InjectionToken, Optional, Inject, Pipe, assertInInjectionContext, inject, DestroyRef, Injectable, NgModule } from '@angular/core';
import Autolinker from 'autolinker';
import { flow, property, toLower, uniqBy } from 'lodash';
import { switchMap, take, tap, of, Subject, Observable, takeUntil, BehaviorSubject, map, distinctUntilChanged } from 'rxjs';
import { DateService } from '@engineering11/date-time';
import * as i1 from '@angular/common/http';
import { CommonModule } from '@angular/common';

/**
 * implements
 * @param simpleChanges @hasChanged from @engineering11/utility for simpleChanges
 * @param key
 * @returns
 */
function hasChangedNg(simpleChanges, key) {
    const simpleChange = simpleChanges[key];
    return hasChanged(key, { before: simpleChange.previousValue, after: simpleChange.currentValue });
}
function hasChangedAnyNg(simpleChanges, keys) {
    return keys.some((key) => hasChangedNg(simpleChanges, key));
}

/**
 * Copies the given data to the clipboard
 * @param toCopy
 */
function copyToClipboard(toCopy) {
    try {
        navigator.clipboard.writeText(toCopy);
    }
    catch (error) {
        console.error(error);
        const selBox = document.createElement('textarea');
        selBox.style.position = 'fixed';
        selBox.style.left = '0';
        selBox.style.top = '0';
        selBox.style.opacity = '0';
        selBox.value = toCopy;
        document.body.appendChild(selBox);
        selBox.focus();
        selBox.select();
        document.execCommand('copy');
        document.body.removeChild(selBox);
    }
}

function stateCodeToOption(code) {
    const name = `${US_STATE_CODE_TO_NAME[code]} (${code})`;
    return { name, value: code };
}
const formSelectOptionsStates = Object.freeze(US_STATE_TERRITORY_CODES.map(stateCodeToOption));
const formSelectOptionsMonths = Object.freeze([
    { name: 'Jan', value: '01' },
    { name: 'Feb', value: '02' },
    { name: 'Mar', value: '03' },
    { name: 'Apr', value: '04' },
    { name: 'May', value: '05' },
    { name: 'Jun', value: '06' },
    { name: 'Jul', value: '07' },
    { name: 'Aug', value: '08' },
    { name: 'Sep', value: '09' },
    { name: 'Oct', value: '10' },
    { name: 'Nov', value: '11' },
    { name: 'Dec', value: '12' },
]);
const formSelectOptionsPastYears = Object.freeze((() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= currentYear - 100; year--) {
        years.push({ name: year.toString(), value: year.toString() });
    }
    return years;
})());
const formSelectOptionsFutureYears = Object.freeze((() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year <= currentYear + 100; year++) {
        years.push({ name: year.toString(), value: year.toString() });
    }
    return years;
})());
const formSelectOptionsGICSIndustryGroup = Object.freeze([
    { name: '', value: '' },
    { name: 'Automobiles and Components', value: 'Automobiles and Components' },
    { name: 'Banks', value: 'Banks' },
    { name: 'Capital Goods', value: 'Capital Goods' },
    { name: 'Commercial and Professional Services', value: 'Commercial and Professional Services' },
    { name: 'Consumer Durables and Apparel', value: 'Consumer Durables and Apparel' },
    { name: 'Consumer Services', value: 'Consumer Services' },
    { name: 'Diversified Financials', value: 'Diversified Financials' },
    { name: 'Energy', value: 'Energy' },
    { name: 'Food, Beverage, and Tobacco', value: 'Food, Beverage, and Tobacco' },
    { name: 'Food and Staples Retailing', value: 'Food and Staples Retailing' },
    { name: 'Health Care Equipment and Services', value: 'Health Care Equipment and Services' },
    { name: 'Household and Personal Products', value: 'Household and Personal Products' },
    { name: 'Insurance', value: 'Insurance' },
    { name: 'Materials', value: 'Materials' },
    { name: 'Media and Entertainment', value: 'Media and Entertainment' },
    {
        name: 'Pharmaceuticals, Biotechnology, and Life Sciences',
        value: 'Pharmaceuticals, Biotechnology, and Life Sciences',
    },
    { name: 'Real Estate', value: 'Real Estate' },
    { name: 'Retailing', value: 'Retailing' },
    { name: 'Semiconductors and Semiconductor Equipment', value: 'Semiconductors and Semiconductor Equipment' },
    { name: 'Software and Services', value: 'Software and Services' },
    { name: 'Technology Hardware and Equipment', value: 'Technology Hardware and Equipment' },
    { name: 'Telecommunication Services', value: 'Telecommunication Services' },
    { name: 'Transportation', value: 'Transportation' },
    { name: 'Utilities', value: 'Utilities' },
]);
const formSelectOptionsBusinessSizeRange = Object.freeze([
    { name: '', value: '' },
    { name: '1-10', value: '1-10' },
    { name: '11-50', value: '11-50' },
    { name: '51-150', value: '51-150' },
    { name: '151-500', value: '151-500' },
    { name: '501-1000', value: '501-1000' },
    { name: '1001-5000', value: '1001-5000' },
    { name: '5001-10,000', value: '5001-10,000' },
    { name: '10,001+', value: '10,001+' },
]);
const formSelectOptionsIndustrySectorList = Object.freeze([
    { name: '', value: '' },
    { name: 'Communication Services', value: 'Communication Services' },
    { name: 'Consumer Discretionary', value: 'Consumer Discretionary' },
    { name: 'Consumer Staples', value: 'Consumer Staples' },
    { name: 'Energy', value: 'Energy' },
    { name: 'Financials', value: 'Financials' },
    { name: 'Health Care', value: 'Health Care' },
    { name: 'Industrials', value: 'Industrials' },
    { name: 'Information Technology', value: 'Information Technology' },
    { name: 'Materials', value: 'Materials' },
    { name: 'Real Estate', value: 'Real Estate' },
    { name: 'Utilities', value: 'Utilities' },
]);
const formSelectOptionsBusinessType = Object.freeze([
    { name: '', value: '' },
    { name: 'Public', value: 'Public' },
    { name: 'Private', value: 'Private' },
]);
const formSelectOptionsCountry = Object.freeze([
    { name: 'United States', value: 'US' },
    { name: 'Afghanistan', value: 'AF' },
    { name: 'Åland Islands', value: 'AX' },
    { name: 'Albania', value: 'AL' },
    { name: 'Algeria', value: 'DZ' },
    { name: 'American Samoa', value: 'AS' },
    { name: 'Andorra', value: 'AD' },
    { name: 'Angola', value: 'AO' },
    { name: 'Anguilla', value: 'AI' },
    { name: 'Antarctica', value: 'AQ' },
    { name: 'Antigua and Barbuda', value: 'AG' },
    { name: 'Argentina', value: 'AR' },
    { name: 'Armenia', value: 'AM' },
    { name: 'Aruba', value: 'AW' },
    { name: 'Australia', value: 'AU' },
    { name: 'Austria', value: 'AT' },
    { name: 'Azerbaijan', value: 'AZ' },
    { name: 'Bahamas', value: 'BS' },
    { name: 'Bahrain', value: 'BH' },
    { name: 'Bangladesh', value: 'BD' },
    { name: 'Barbados', value: 'BB' },
    { name: 'Belarus', value: 'BY' },
    { name: 'Belgium', value: 'BE' },
    { name: 'Belize', value: 'BZ' },
    { name: 'Benin', value: 'BJ' },
    { name: 'Bermuda', value: 'BM' },
    { name: 'Bhutan', value: 'BT' },
    { name: 'Bolivia', value: 'BO' },
    { name: 'Bosnia and Herzegovina', value: 'BA' },
    { name: 'Botswana', value: 'BW' },
    { name: 'Bouvet Island', value: 'BV' },
    { name: 'Brazil', value: 'BR' },
    { name: 'British Indian Ocean Territory', value: 'IO' },
    { name: 'Brunei Darussalam', value: 'BN' },
    { name: 'Bulgaria', value: 'BG' },
    { name: 'Burkina Faso', value: 'BF' },
    { name: 'Burundi', value: 'BI' },
    { name: 'Cambodia', value: 'KH' },
    { name: 'Cameroon', value: 'CM' },
    { name: 'Canada', value: 'CA' },
    { name: 'Cape Verde', value: 'CV' },
    { name: 'Cayman Islands', value: 'KY' },
    { name: 'Central African Republic', value: 'CF' },
    { name: 'Chad', value: 'TD' },
    { name: 'Chile', value: 'CL' },
    { name: 'China', value: 'CN' },
    { name: 'Christmas Island', value: 'CX' },
    { name: 'Cocos (Keeling) Islands', value: 'CC' },
    { name: 'Colombia', value: 'CO' },
    { name: 'Comoros', value: 'KM' },
    { name: 'Congo', value: 'CG' },
    { name: 'Congo, The Democratic Republic of the', value: 'CD' },
    { name: 'Cook Islands', value: 'CK' },
    { name: 'Costa Rica', value: 'CR' },
    { name: "Cote D'Ivoire", value: 'CI' },
    { name: 'Croatia', value: 'HR' },
    { name: 'Cuba', value: 'CU' },
    { name: 'Cyprus', value: 'CY' },
    { name: 'Czech Republic', value: 'CZ' },
    { name: 'Denmark', value: 'DK' },
    { name: 'Djibouti', value: 'DJ' },
    { name: 'Dominica', value: 'DM' },
    { name: 'Dominican Republic', value: 'DO' },
    { name: 'Ecuador', value: 'EC' },
    { name: 'Egypt', value: 'EG' },
    { name: 'El Salvador', value: 'SV' },
    { name: 'Equatorial Guinea', value: 'GQ' },
    { name: 'Eritrea', value: 'ER' },
    { name: 'Estonia', value: 'EE' },
    { name: 'Ethiopia', value: 'ET' },
    { name: 'Falkland Islands (Malvinas)', value: 'FK' },
    { name: 'Faroe Islands', value: 'FO' },
    { name: 'Fiji', value: 'FJ' },
    { name: 'Finland', value: 'FI' },
    { name: 'France', value: 'FR' },
    { name: 'French Guiana', value: 'GF' },
    { name: 'French Polynesia', value: 'PF' },
    { name: 'French Southern Territories', value: 'TF' },
    { name: 'Gabon', value: 'GA' },
    { name: 'Gambia', value: 'GM' },
    { name: 'Georgia', value: 'GE' },
    { name: 'Germany', value: 'DE' },
    { name: 'Ghana', value: 'GH' },
    { name: 'Gibraltar', value: 'GI' },
    { name: 'Greece', value: 'GR' },
    { name: 'Greenland', value: 'GL' },
    { name: 'Grenada', value: 'GD' },
    { name: 'Guadeloupe', value: 'GP' },
    { name: 'Guam', value: 'GU' },
    { name: 'Guatemala', value: 'GT' },
    { name: 'Guernsey', value: 'GG' },
    { name: 'Guinea', value: 'GN' },
    { name: 'Guinea-Bissau', value: 'GW' },
    { name: 'Guyana', value: 'GY' },
    { name: 'Haiti', value: 'HT' },
    { name: 'Heard Island and Mcdonald Islands', value: 'HM' },
    { name: 'Holy See (Vatican City State)', value: 'VA' },
    { name: 'Honduras', value: 'HN' },
    { name: 'Hong Kong', value: 'HK' },
    { name: 'Hungary', value: 'HU' },
    { name: 'Iceland', value: 'IS' },
    { name: 'India', value: 'IN' },
    { name: 'Indonesia', value: 'ID' },
    { name: 'Iran, Islamic Republic Of', value: 'IR' },
    { name: 'Iraq', value: 'IQ' },
    { name: 'Ireland', value: 'IE' },
    { name: 'Isle of Man', value: 'IM' },
    { name: 'Israel', value: 'IL' },
    { name: 'Italy', value: 'IT' },
    { name: 'Jamaica', value: 'JM' },
    { name: 'Japan', value: 'JP' },
    { name: 'Jersey', value: 'JE' },
    { name: 'Jordan', value: 'JO' },
    { name: 'Kazakhstan', value: 'KZ' },
    { name: 'Kenya', value: 'KE' },
    { name: 'Kiribati', value: 'KI' },
    { name: "Korea, Democratic People's Republic of", value: 'KP' },
    { name: 'Korea, Republic of', value: 'KR' },
    { name: 'Kuwait', value: 'KW' },
    { name: 'Kyrgyzstan', value: 'KG' },
    { name: "Lao People's Democratic Republic", value: 'LA' },
    { name: 'Latvia', value: 'LV' },
    { name: 'Lebanon', value: 'LB' },
    { name: 'Lesotho', value: 'LS' },
    { name: 'Liberia', value: 'LR' },
    { name: 'Libyan Arab Jamahiriya', value: 'LY' },
    { name: 'Liechtenstein', value: 'LI' },
    { name: 'Lithuania', value: 'LT' },
    { name: 'Luxembourg', value: 'LU' },
    { name: 'Macao', value: 'MO' },
    { name: 'Macedonia, The Former Yugoslav Republic of', value: 'MK' },
    { name: 'Madagascar', value: 'MG' },
    { name: 'Malawi', value: 'MW' },
    { name: 'Malaysia', value: 'MY' },
    { name: 'Maldives', value: 'MV' },
    { name: 'Mali', value: 'ML' },
    { name: 'Malta', value: 'MT' },
    { name: 'Marshall Islands', value: 'MH' },
    { name: 'Martinique', value: 'MQ' },
    { name: 'Mauritania', value: 'MR' },
    { name: 'Mauritius', value: 'MU' },
    { name: 'Mayotte', value: 'YT' },
    { name: 'Mexico', value: 'MX' },
    { name: 'Micronesia, Federated States of', value: 'FM' },
    { name: 'Moldova, Republic of', value: 'MD' },
    { name: 'Monaco', value: 'MC' },
    { name: 'Mongolia', value: 'MN' },
    { name: 'Montserrat', value: 'MS' },
    { name: 'Morocco', value: 'MA' },
    { name: 'Mozambique', value: 'MZ' },
    { name: 'Myanmar', value: 'MM' },
    { name: 'Namibia', value: 'NA' },
    { name: 'Nauru', value: 'NR' },
    { name: 'Nepal', value: 'NP' },
    { name: 'Netherlands', value: 'NL' },
    { name: 'Netherlands Antilles', value: 'AN' },
    { name: 'New Caledonia', value: 'NC' },
    { name: 'New Zealand', value: 'NZ' },
    { name: 'Nicaragua', value: 'NI' },
    { name: 'Niger', value: 'NE' },
    { name: 'Nigeria', value: 'NG' },
    { name: 'Niue', value: 'NU' },
    { name: 'Norfolk Island', value: 'NF' },
    { name: 'Northern Mariana Islands', value: 'MP' },
    { name: 'Norway', value: 'NO' },
    { name: 'Oman', value: 'OM' },
    { name: 'Pakistan', value: 'PK' },
    { name: 'Palau', value: 'PW' },
    { name: 'Palestinian Territory, Occupied', value: 'PS' },
    { name: 'Panama', value: 'PA' },
    { name: 'Papua New Guinea', value: 'PG' },
    { name: 'Paraguay', value: 'PY' },
    { name: 'Peru', value: 'PE' },
    { name: 'Philippines', value: 'PH' },
    { name: 'Pitcairn', value: 'PN' },
    { name: 'Poland', value: 'PL' },
    { name: 'Portugal', value: 'PT' },
    { name: 'Puerto Rico', value: 'PR' },
    { name: 'Qatar', value: 'QA' },
    { name: 'Reunion', value: 'RE' },
    { name: 'Romania', value: 'RO' },
    { name: 'Russian Federation', value: 'RU' },
    { name: 'Rwanda', value: 'RW' },
    { name: 'Saint Helena', value: 'SH' },
    { name: 'Saint Kitts and Nevis', value: 'KN' },
    { name: 'Saint Lucia', value: 'LC' },
    { name: 'Saint Pierre and Miquelon', value: 'PM' },
    { name: 'Saint Vincent and the Grenadines', value: 'VC' },
    { name: 'Samoa', value: 'WS' },
    { name: 'San Marino', value: 'SM' },
    { name: 'Sao Tome and Principe', value: 'ST' },
    { name: 'Saudi Arabia', value: 'SA' },
    { name: 'Senegal', value: 'SN' },
    { name: 'Serbia and Montenegro', value: 'CS' },
    { name: 'Seychelles', value: 'SC' },
    { name: 'Sierra Leone', value: 'SL' },
    { name: 'Singapore', value: 'SG' },
    { name: 'Slovakia', value: 'SK' },
    { name: 'Slovenia', value: 'SI' },
    { name: 'Solomon Islands', value: 'SB' },
    { name: 'Somalia', value: 'SO' },
    { name: 'South Africa', value: 'ZA' },
    { name: 'South Georgia and the South Sandwich Islands', value: 'GS' },
    { name: 'Spain', value: 'ES' },
    { name: 'Sri Lanka', value: 'LK' },
    { name: 'Sudan', value: 'SD' },
    { name: 'Suriname', value: 'SR' },
    { name: 'Svalbard and Jan Mayen', value: 'SJ' },
    { name: 'Swaziland', value: 'SZ' },
    { name: 'Sweden', value: 'SE' },
    { name: 'Switzerland', value: 'CH' },
    { name: 'Syrian Arab Republic', value: 'SY' },
    { name: 'Taiwan, Province of China', value: 'TW' },
    { name: 'Tajikistan', value: 'TJ' },
    { name: 'Tanzania, United Republic of', value: 'TZ' },
    { name: 'Thailand', value: 'TH' },
    { name: 'Timor-Leste', value: 'TL' },
    { name: 'Togo', value: 'TG' },
    { name: 'Tokelau', value: 'TK' },
    { name: 'Tonga', value: 'TO' },
    { name: 'Trinidad and Tobago', value: 'TT' },
    { name: 'Tunisia', value: 'TN' },
    { name: 'Turkey', value: 'TR' },
    { name: 'Turkmenistan', value: 'TM' },
    { name: 'Turks and Caicos Islands', value: 'TC' },
    { name: 'Tuvalu', value: 'TV' },
    { name: 'Uganda', value: 'UG' },
    { name: 'Ukraine', value: 'UA' },
    { name: 'United Arab Emirates', value: 'AE' },
    { name: 'United Kingdom', value: 'GB' },
    { name: 'United States Minor Outlying Islands', value: 'UM' },
    { name: 'Uruguay', value: 'UY' },
    { name: 'Uzbekistan', value: 'UZ' },
    { name: 'Vanuatu', value: 'VU' },
    { name: 'Venezuela', value: 'VE' },
    { name: 'Viet Nam', value: 'VN' },
    { name: 'Virgin Islands, British', value: 'VG' },
    { name: 'Virgin Islands, U.S.', value: 'VI' },
    { name: 'Wallis and Futuna', value: 'WF' },
    { name: 'Western Sahara', value: 'EH' },
    { name: 'Yemen', value: 'YE' },
    { name: 'Zambia', value: 'ZM' },
    { name: 'Zimbabwe', value: 'ZW' },
]);

/**
 *
 * @returns true if the user is on a mobile device and false if not
 */
function isMobile() {
    let check = false;
    (function (a) {
        if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a) ||
            /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4)))
            check = true;
        // @ts-ignore
    })(navigator.userAgent || navigator.vendor || window.opera);
    return check;
}
/**
 *
 * @returns true if the user is on a mobile device or tablet and false if not
 */
function isMobileOrTablet() {
    let check = false;
    (function (a) {
        if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(a) ||
            /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4)))
            check = true;
        // @ts-ignore
    })(navigator.userAgent || navigator.vendor || window.opera);
    return check;
}

class ClickOutsideDirective {
    constructor(elementRef) {
        this.elementRef = elementRef;
        this.clickOutside = new EventEmitter();
    }
    onClick(target) {
        const clickedInside = this.elementRef.nativeElement.contains(target);
        if (!clickedInside) {
            this.clickOutside.emit();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ClickOutsideDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: ClickOutsideDirective, isStandalone: true, selector: "[clickOutsideClose]", outputs: { clickOutside: "clickOutside" }, host: { listeners: { "document:click": "onClick($event.target)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ClickOutsideDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[clickOutsideClose]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { clickOutside: [{
                type: Output
            }], onClick: [{
                type: HostListener,
                args: ['document:click', ['$event.target']]
            }] } });

class DropdownCloserDirective {
    constructor(elementRef) {
        this.elementRef = elementRef;
        this.closeEventTriggered = new EventEmitter();
    }
    onClick(targetElement) {
        const clickedInside = this.elementRef.nativeElement.contains(targetElement) || this.control.contains(targetElement);
        if (!clickedInside) {
            this.closeEventTriggered.emit(true);
        }
    }
    onKeydownHandler(event) {
        if (event.key === 'Escape') {
            this.closeEventTriggered.emit(true);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DropdownCloserDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: DropdownCloserDirective, isStandalone: true, selector: "[dropdownCloser]", inputs: { control: ["dropdownCloser", "control"] }, outputs: { closeEventTriggered: "closeEventTriggered" }, host: { listeners: { "document:click": "onClick($event.target)", "document:keydown": "onKeydownHandler($event)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DropdownCloserDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dropdownCloser]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { control: [{
                type: Input,
                args: ['dropdownCloser']
            }], closeEventTriggered: [{
                type: Output
            }], onClick: [{
                type: HostListener,
                args: ['document:click', ['$event.target']]
            }], onKeydownHandler: [{
                type: HostListener,
                args: ['document:keydown', ['$event']]
            }] } });

class EscapeKeyDirective {
    constructor() {
        this.escapeKey = new EventEmitter();
    }
    onKeydownHandler(event) {
        this.escapeKey.emit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: EscapeKeyDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: EscapeKeyDirective, isStandalone: true, selector: "[escapeKeyClose]", outputs: { escapeKey: "escapeKey" }, host: { listeners: { "document:keydown.escape": "onKeydownHandler($event.target)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: EscapeKeyDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[escapeKeyClose]',
                    standalone: true,
                }]
        }], propDecorators: { escapeKey: [{
                type: Output
            }], onKeydownHandler: [{
                type: HostListener,
                args: ['document:keydown.escape', ['$event.target']]
            }] } });

const RESIZABLE_IMAGE_CONFIG = new InjectionToken('ResizableImageConfig');

class ResizableImageDirective {
    constructor(config, el) {
        this.config = config;
        this.el = el;
        this.width = undefined; // using string instead of number to allow units e.g 120px
        this.height = undefined;
        this.fit = undefined;
    }
    ngOnInit() {
        const cdnUrl = this.config?.cdnUrl;
        if (!cdnUrl) {
            return;
        }
        const img = this.el.nativeElement;
        const url = img.src;
        img.src = cdnUrl
            .replace('{e11-width}', this.width ?? '')
            .replace('{e11-fit}', this.fit ?? '')
            .replace('{e11-height}', this.height ?? '')
            .replace('{e11-originalUrl}', url);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ResizableImageDirective, deps: [{ token: RESIZABLE_IMAGE_CONFIG, optional: true }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: ResizableImageDirective, isStandalone: true, selector: "[resizableImage]", inputs: { width: ["resizableImageWidth", "width"], height: ["resizableImageHeight", "height"], fit: ["resizableImageFit", "fit"] }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ResizableImageDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[resizableImage]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [RESIZABLE_IMAGE_CONFIG]
                }] }, { type: i0.ElementRef }], propDecorators: { width: [{
                type: Input,
                args: ['resizableImageWidth']
            }], height: [{
                type: Input,
                args: ['resizableImageHeight']
            }], fit: [{
                type: Input,
                args: ['resizableImageFit']
            }] } });

class DisplayPager {
    constructor(innerList) {
        this.innerList = innerList;
        this.currentIndex = 0;
        if (innerList.length === 0) {
            throw new Error('Must provide a list with at least 1 member');
        }
        this.findNextValidItem();
    }
    getCurrent() {
        return this.getItemAtIndex(this.currentIndex);
    }
    getItemAtIndex(index) {
        if (index >= this.innerList.length || index < 0) {
            return null;
        }
        return this.innerList[index] ?? null;
    }
    next() {
        this.currentIndex++;
        this.findNextValidItem();
        return this.getCurrent();
    }
    previous() {
        this.currentIndex--;
        this.findPreviousValidItem();
        return this.getCurrent();
    }
    last() {
        return this.innerList[this.innerList.length - 1] || null;
    }
    updateItem(matchingFn, update) {
        this.innerList = updateObject(matchingFn, update, this.innerList);
    }
    displayList() {
        return this.innerList.filter((item) => item.display !== false);
    }
    goTo(matcherFn) {
        const idx = this.innerList.findIndex(matcherFn);
        if (idx !== -1) {
            this.currentIndex = idx;
        }
    }
    peekNext() {
        let lookForward = 1;
        const peekIndex = () => this.currentIndex + lookForward;
        while (this.shouldNotDisplayItemAtIndex(peekIndex())) {
            lookForward++;
        }
        return this.getItemAtIndex(peekIndex());
    }
    peekPrevious() {
        let lookBack = -1;
        const peekIndex = () => this.currentIndex + lookBack;
        while (this.shouldNotDisplayItemAtIndex(peekIndex())) {
            lookBack--;
        }
        return this.getItemAtIndex(peekIndex());
    }
    reset() {
        this.currentIndex = 0;
        this.findNextValidItem();
    }
    findNextValidItem() {
        while (this.shouldNotDisplayItemAtIndex(this.currentIndex)) {
            this.currentIndex++;
        }
    }
    findPreviousValidItem() {
        while (this.shouldNotDisplayItemAtIndex(this.currentIndex)) {
            this.currentIndex--;
        }
    }
    shouldNotDisplayItemAtIndex(index) {
        return this.innerList[index]?.display === false && index >= 0 && index < this.innerList.length;
    }
}

var ImageTypeEnum;
(function (ImageTypeEnum) {
    ImageTypeEnum["PNG"] = "image/png";
    ImageTypeEnum["JPEG"] = "image/jpeg";
    ImageTypeEnum["GIF"] = "image/gif";
})(ImageTypeEnum || (ImageTypeEnum = {}));

function checkImageType(type) {
    return ['image/png', 'image/jpeg', 'image/gif'].some((i) => i === type);
}

const urlToBlob = (url) => {
    return fetch(url).then((response) => response.blob());
};
const fileToDataURL = (file) => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = (e) => resolve(e.target.result);
        reader.readAsDataURL(file);
    });
};
const dataURLtoImage = (dataURL) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('data URL is illegal'));
        img.src = dataURL;
    });
};
const dataURLtoFile = (dataURL, type) => {
    const arr = dataURL.split(',');
    const intermediate = arr[0].match(/:(.*?);/);
    let mime = intermediate[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    if (checkImageType(type)) {
        mime = type;
    }
    return new Blob([u8arr], {
        type: mime,
    });
};
function canvasToFile(canvas, quality = 0.92, type = ImageTypeEnum.JPEG) {
    return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), type, quality));
}

/**
 * Checks whether the dimensions of the provided image file meet the specified criteria.
 * @param image - The image file for which dimensions are to be validated.
 * @param targetDimension - The target dimensions that the image should meet.
 * @param exact - A boolean indicating whether the dimensions should match exactly or be greater than or equal.
 * @returns A Promise that resolves to true if the image dimensions are valid, and false otherwise.
 */
async function validateImageDimensions(image, targetDimension, exact = true) {
    const { width, height } = await getImageDimensions(image);
    const { width: targetWidth, height: targetHeight } = targetDimension;
    if (exact) {
        return width === targetWidth && height === targetHeight;
    }
    return width >= targetWidth && height >= targetHeight;
}
/**
 * Retrieves the dimensions (width and height) of an image file asynchronously.
 * @param image - The image file for which dimensions are to be obtained.
 * @returns A Promise that resolves to an object containing the width and height of the image.
 */
function getImageDimensions(image) {
    return new Promise((resolve) => {
        const img = new Image();
        img.src = URL.createObjectURL(image);
        img.onload = () => {
            const { width, height } = img;
            resolve({ width, height });
        };
    });
}

const mimeTypeIconMap = {
    // Media
    image: 'image',
    audio: 'audio_file',
    video: 'video_file',
    // Documents
    'application/pdf': 'picture_as_pdf',
    'application/msword': 'description',
    'application/vnd.ms-word': 'description',
    'application/vnd.oasis.opendocument.text': 'description',
    'application/vnd.openxmlformats-officedocument.wordprocessingml': 'description',
    'application/vnd.ms-excel': 'description',
    'application/vnd.openxmlformats-officedocument.spreadsheetml': 'description',
    'application/vnd.oasis.opendocument.spreadsheet': 'description',
    'application/vnd.ms-powerpoint': 'description',
    'application/vnd.openxmlformats-officedocument.presentationml': 'description',
    'application/vnd.oasis.opendocument.presentation': 'description',
    'text/plain': 'description',
    'text/html': 'html',
    'application/json': 'javascript',
    // Archives
    'application/gzip': 'folder_zip',
    'application/zip': 'folder_zip',
    //unknown
    unknown: 'insert_drive_file',
};

var FileMimeTypeIconTypes;
(function (FileMimeTypeIconTypes) {
    FileMimeTypeIconTypes["Audio"] = "Audio";
    FileMimeTypeIconTypes["Video"] = "Video";
    FileMimeTypeIconTypes["Preview"] = "Preview";
    FileMimeTypeIconTypes["Unknown"] = "Unknown";
})(FileMimeTypeIconTypes || (FileMimeTypeIconTypes = {}));

const getFileMimeType = (file) => {
    return file.type;
};
function findFileIcon(mimeType) {
    let icon;
    Object.keys(mimeTypeIconMap).forEach((key) => {
        if (mimeType.includes(key)) {
            icon = mimeTypeIconMap[key];
        }
    });
    if (!icon) {
        return mimeTypeIconMap['unknown'];
    }
    if (icon === 'image') {
        return FileMimeTypeIconTypes.Preview;
    }
    return icon;
}

// Standard File Constants
const STRINGS = {
    symbol: {
        iec: {
            bits: ['bit', 'Kibit', 'Mibit', 'Gibit', 'Tibit', 'Pibit', 'Eibit', 'Zibit', 'Yibit'],
            bytes: ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'],
        },
        jedec: {
            bits: ['bit', 'Kbit', 'Mbit', 'Gbit', 'Tbit', 'Pbit', 'Ebit', 'Zbit', 'Ybit'],
            bytes: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
        },
    },
    fullform: {
        iec: ['', 'kibi', 'mebi', 'gibi', 'tebi', 'pebi', 'exbi', 'zebi', 'yobi'],
        jedec: ['', 'kilo', 'mega', 'giga', 'tera', 'peta', 'exa', 'zetta', 'yotta'],
    },
};
const ARRAY = 'array';
const BIT = 'bit';
const BITS = 'bits';
const BYTE = 'byte';
const BYTES = 'bytes';
const EMPTY = '';
const EXPONENT = 'exponent';
const FUNCTION = 'function';
const IEC = 'iec';
const INVALID_NUMBER = 'Invalid number';
const INVALID_ROUND = 'Invalid rounding method';
const JEDEC = 'jedec';
const OBJECT = 'object';
const PERIOD = '.';
const ROUND = 'round';
const S = 's';
const SI_KBIT = 'kbit';
const SI_KBYTE = 'kB';
const SPACE = ' ';
const STRING = 'string';
const ZERO = '0';

const _fileSize = (_props, _args) => {
    let { bits = false, pad = false, base = -1, round = 2, locale = EMPTY, localeOptions = {}, separator = EMPTY, spacer = SPACE, symbols = {}, standard = EMPTY, output = STRING, fullform = false, fullforms = [], exponent = -1, roundingMethod = ROUND, precision = 0, } = _args;
    let _exponent = exponent, _number = Number(_props), result = [], val = 0, unit = EMPTY;
    // map standard
    if (base === -1 && standard.length === 0) {
        base = 10;
        standard = JEDEC;
    }
    else if (base === -1 && standard.length > 0) {
        standard = standard === IEC ? IEC : JEDEC;
        base = standard === IEC ? 2 : 10;
    }
    else {
        base = base === 2 ? 2 : 10;
        standard = base === 10 ? JEDEC : standard === JEDEC ? JEDEC : IEC;
    }
    const ceil = base === 10 ? 1000 : 1024;
    const full = fullform === true;
    const neg = _number < 0;
    const roundingFunc = Math[roundingMethod];
    if (typeof _props !== 'bigint' && isNaN(_props)) {
        throw new TypeError(INVALID_NUMBER);
    }
    if (typeof roundingFunc !== FUNCTION) {
        throw new TypeError(INVALID_ROUND);
    }
    if (neg) {
        _number = -_number;
    }
    if (_exponent === -1 || isNaN(_exponent)) {
        _exponent = Math.floor(Math.log(_number) / Math.log(ceil));
        if (_exponent < 0) {
            _exponent = 0;
        }
    }
    if (_exponent > 8) {
        if (precision > 0) {
            precision += 8 - _exponent;
        }
        _exponent = 8;
    }
    if (output === EXPONENT) {
        return _exponent;
    }
    // check for zero because bytes divide by 1
    if (_number === 0) {
        result[0] = 0;
        const symbol = STRINGS.symbol;
        unit = result[1] = symbol[standard][bits ? BITS : BYTES][_exponent];
    }
    else {
        val = _number / (base === 2 ? Math.pow(2, _exponent * 10) : Math.pow(1000, _exponent));
        if (bits) {
            val = val * 8;
            if (val >= ceil && _exponent < 8) {
                val = val / ceil;
                _exponent++;
            }
        }
        const p = Math.pow(10, _exponent > 0 ? round : 0);
        result[0] = roundingFunc(val * p) / p;
        if (result[0] === ceil && _exponent < 8 && exponent === -1) {
            result[0] = 1;
            _exponent++;
        }
        const symbol = STRINGS.symbol;
        unit = result[1] = base === 10 && _exponent === 1 ? (bits ? SI_KBIT : SI_KBYTE) : symbol[standard][bits ? BITS : BYTES][_exponent];
    }
    if (neg) {
        result[0] = -result[0];
    }
    if (precision > 0) {
        result[0] = result[0].toPrecision(precision);
    }
    result[1] = symbols[result[1]] || result[1];
    if (locale === true) {
        result[0] = result[0].toLocaleString();
    }
    else if (locale.length > 0) {
        result[0] = result[0].toLocaleString(locale, localeOptions);
    }
    else if (separator.length > 0) {
        result[0] = result[0].toString().replace(PERIOD, separator);
    }
    if (pad && Number.isInteger(result[0]) === false && round > 0) {
        const x = separator || PERIOD, tmp = result[0].toString().split(x), s = tmp[1] || EMPTY, l = s.length, n = round - l;
        result[0] = `${tmp[0]}${x}${s.padEnd(l + n, ZERO)}`;
    }
    if (full) {
        const fullForm = STRINGS.fullform;
        result[1] = fullforms[_exponent] ? fullforms[_exponent] : fullForm[standard][_exponent] + (bits ? BIT : BYTE) + (result[0] === 1 ? EMPTY : S);
    }
    return output === ARRAY
        ? result
        : output === OBJECT
            ? {
                value: result[0],
                symbol: result[1],
                exponent: _exponent,
                unit: unit,
            }
            : result.join(spacer);
};

function fileSize({ bits = false, pad = false, base = -1, round = 2, locale = EMPTY, localeOptions = {}, separator = EMPTY, spacer = SPACE, symbols = {}, standard = EMPTY, output = STRING, fullform = false, fullforms = [], exponent = -1, roundingMethod = ROUND, precision = 0, } = {}) {
    return (arg) => _fileSize(arg, {
        bits,
        pad,
        base,
        round,
        locale,
        localeOptions,
        separator,
        spacer,
        symbols,
        standard,
        output,
        fullform,
        fullforms,
        exponent,
        roundingMethod,
        precision,
    });
}
const formattedSize = fileSize({ base: 2, standard: 'jedec' });

/**
 * Filters a given array for a string using specified keys
 *
 * @deprecated use filterByKeys - more descriptive name
 */
const filter = filterByKeys;
/**
 * @deprecated use utility function @includesMatch
 */
const textMatches = includesMatch;

/** @deprecated - use the IFormSelectOptions interface */
class FormSelectModel {
    constructor(value, name) {
        this.value = value;
        this.name = name;
    }
}

const mappingToFormSelect = (mapping, transform = display => display) => Object.entries(mapping).map(([value, display]) => new FormSelectModel(value, transform(display)));

const States = {
    AK: 'Alaska',
    AL: 'Alabama',
    AR: 'Arkansas',
    AZ: 'Arizona',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DC: 'District of Columbia',
    DE: 'Delaware',
    FL: 'Florida',
    GA: 'Georgia',
    HI: 'Hawaii',
    IA: 'Iowa',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    MA: 'Massachusetts',
    MD: 'Maryland',
    ME: 'Maine',
    MI: 'Michigan',
    MN: 'Minnesota',
    MO: 'Missouri',
    MS: 'Mississippi',
    MT: 'Montana',
    NC: 'North Carolina',
    ND: 'North Dakota',
    NE: 'Nebraska',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NV: 'Nevada',
    NY: 'New York',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VA: 'Virginia',
    VT: 'Vermont',
    WA: 'Washington',
    WI: 'Wisconsin',
    WV: 'West Virginia',
    WY: 'Wyoming',
};

const Time = {
    '1:00 AM': '1:00 AM',
    '1:00 PM': '1:00 PM',
    '2:00 AM': '2:00 AM',
    '2:00 PM': '2:00 PM',
    '3:00 AM': '3:00 AM',
    '3:00 PM': '3:00 PM',
    '4:00 AM': '4:00 AM',
    '4:00 PM': '4:00 PM',
    '5:00 AM': '5:00 AM',
    '5:00 PM': '5:00 PM',
    '6:00 AM': '6:00 AM',
    '6:00 PM': '6:00 PM',
    '7:00 AM': '7:00 AM',
    '7:00 PM': '7:00 PM',
    '8:00 AM': '8:00 AM',
    '8:00 PM': '8:00 PM',
    '9:00 AM': '9:00 AM',
    '9:00 PM': '9:00 PM',
    '10:00 AM': '10:00 AM',
    '10:00 PM': '10:00 PM',
    '11:00 AM': '11:00 AM',
    '11:00 PM': '11:00 PM',
    '12:00 AM': '12:00 AM',
    '12:00 PM': '12:00 PM',
};

class DeepCopyPipe {
    transform(value) {
        return deepCopy(value);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DeepCopyPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: DeepCopyPipe, isStandalone: true, name: "deepCopy" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DeepCopyPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'deepCopy', standalone: true }]
        }] });

class InitialsPipe {
    transform(value) {
        return getInitials(value);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: InitialsPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: InitialsPipe, isStandalone: true, name: "initials" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: InitialsPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'initials', standalone: true }]
        }] });

/**
 * Invoke the given function on the value, gaining the benefits of memoization and removing the usual performance drawbacks of using a function in a template
 */
class InvokePipe {
    transform(value, functionToInvoke) {
        return functionToInvoke(value);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: InvokePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: InvokePipe, isStandalone: true, name: "invoke" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: InvokePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'invoke', standalone: true }]
        }] });

/**
 * Caps the number displayed at the provided limit (defaults to 9)
 */
class LimitPipe {
    transform(value, limit = 9) {
        return value <= limit ? value : `${limit}+`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LimitPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: LimitPipe, isStandalone: true, name: "limit" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LimitPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'limit', standalone: true }]
        }] });

class LinkifyPipe {
    transform(text) {
        const autolinker = new Autolinker({ stripPrefix: false, sanitizeHtml: true, truncate: 50 });
        const linkedText = autolinker.link(text);
        return linkedText;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LinkifyPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: LinkifyPipe, isStandalone: true, name: "linkify" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LinkifyPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'linkify',
                    standalone: true,
                }]
        }] });

/**
 * Converts a number (e.g 3) to an array of that length (e.g. [0, 1, 2]) - using @range under the covers
 * Useful for generating duplicates (such as for a skeleton loader)
 */
class RangePipe {
    transform(count, startAt = 0) {
        return range(count).map((i) => i + startAt);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: RangePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: RangePipe, isStandalone: true, name: "range" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: RangePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'range', standalone: true }]
        }] });

/**
 * NumberCompare pipe
 *
 * @example
 * score | numberCompare : '>=' : 85
 * score | numberCompare : '><' : 69 : 85,
 */
class NumberComparePipe {
    transform(value1, operator, value2, value3) {
        if (isNil(value1))
            return false;
        switch (operator) {
            case '=':
                return value1 === value2;
            case '>':
                return value1 > value2;
            case '<':
                return value1 < value2;
            case '>=':
                return value1 >= value2;
            case '<=':
                return value1 <= value2;
            case '!=':
                return value1 !== value2;
            case '><':
                return value1 > value2 && value1 < (value3 ?? Infinity);
            default:
                return false;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: NumberComparePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: NumberComparePipe, isStandalone: true, name: "numberCompare" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: NumberComparePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'numberCompare',
                    standalone: true,
                }]
        }] });

class TruncatePipe {
    transform(value, limit = 30) {
        const ellipsis = value.length > limit ? '...' : '';
        return value.substring(0, limit) + ellipsis;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TruncatePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: TruncatePipe, isStandalone: true, name: "truncate" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TruncatePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'truncate', standalone: true }]
        }] });

/**
 * Should only be used on keys with string attributes
 * @param array
 * @param key
 * @returns
 */
function removeDuplicatesByKeyIgnoreCase(array, key) {
    const byLowerCaseKey = flow(property(key), toLower);
    return uniqBy(array, byLowerCaseKey);
}
function removeDuplicatesByNameIgnoreCase(array) {
    return removeDuplicatesByKeyIgnoreCase(array, 'name');
}

/**
 * This allows us to prevent components from being reloaded on route change
 * Reuse of a component can be turned on using {shouldReuse: true} on the route data object
 * This is useful if we want to prevent pages from being reloaded on navigation
 */
class E11RouteReuseStrategy {
    constructor() {
        this.handlers = {};
    }
    shouldDetach(route) {
        return route.data.shouldReuse || false;
    }
    store(route, handle) {
        if (route.data.shouldReuse && route?.routeConfig?.path) {
            this.handlers[route.routeConfig.path] = handle;
        }
    }
    shouldAttach(route) {
        if (route?.routeConfig?.path) {
            return !!route.routeConfig && !!this.handlers[route.routeConfig.path];
        }
        return false;
    }
    retrieve(route) {
        if (!route.routeConfig || !route.routeConfig.path)
            return null;
        return this.handlers[route.routeConfig.path];
    }
    shouldReuseRoute(future, curr) {
        return future.data.shouldReuse || false;
    }
}

function openInNewTab(url) {
    window.open(url, '_blank')?.focus();
}

/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Dispatches an action if the loaded$ observable is false.
 *
 * Optionally logs the action name if the logger is provided.
 * @param loaded$
 * @param action
 * @param logger
 * @returns
 */
function dispatchIfNotLoaded(loaded$, action, logger) {
    return (source) => source.pipe(switchMap(() => loaded$.pipe(take(1), tap((loaded) => {
        if (!loaded)
            return action();
        logger?.log(`Store: ${action.name} already dispatched, no need to re-fetch`);
        return of(null);
    }))));
}
/**
 * injects the DestroyRef and returns the onDestroy observable
 * @returns
 */
function destroyNotifier() {
    assertInInjectionContext(destroyNotifier);
    const destroy = new Subject();
    inject(DestroyRef).onDestroy(() => {
        destroy.next();
        destroy.complete();
    });
    return destroy;
}
/**
 * Automatically unsubscribes from the source observable when the component is destroyed
 * @param destroyRef
 * @returns
 */
function autoDestroy(destroyRef) {
    if (!destroyRef) {
        assertInInjectionContext(autoDestroy);
        destroyRef = inject(DestroyRef);
    }
    const destroyed$ = new Observable((subscriber) => {
        return destroyRef.onDestroy(() => subscriber.next());
    });
    return (source) => source.pipe(takeUntil(destroyed$));
}

class CookieService {
    getCookie(name) {
        let ca = document.cookie.split(';');
        let caLen = ca.length;
        let cookieName = `${name}=`;
        let c;
        for (let i = 0; i < caLen; i += 1) {
            c = ca[i].replace(/^\s+/g, '');
            if (c.indexOf(cookieName) == 0) {
                return c.substring(cookieName.length, c.length);
            }
        }
        return '';
    }
    deleteCookie(cookieName) {
        this.setCookie({ name: cookieName, value: '', expireDays: -1 });
    }
    /**
     * Expires default 1 day
     * If params.session is set and true expires is not added
     * If params.path is not set or value is not greater than 0 its default value will be root "/"
     * Secure flag can be activated only with https implemented
     * Examples of usage:
     * {service instance}.setCookie({name:'token',value:'abcd12345', session:true, expireDays:10  }); <- This cookie will not expire
     */
    setCookie(params) {
        let d = new Date();
        d.setTime(d.getTime() + (params.expireDays ? params.expireDays : 1) * 24 * 60 * 60 * 1000);
        document.cookie =
            (params.name ? params.name : '') +
                '=' +
                (params.value ? params.value : '') +
                ';' +
                (params.session && params.session == true ? '' : 'expires=' + d.toUTCString() + ';') +
                `domain=.${location.hostname};` +
                'path=' +
                (params.path && params.path.length > 0 ? params.path : `/`) +
                ';' +
                (location.protocol === 'https:' && params.secure && params.secure == true ? 'secure' : '');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CookieService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CookieService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CookieService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class UiDateFromPartService {
    constructor() { }
    processDate(year, month) {
        let response = null;
        // Add a forward slash and/or timestamp to the date to bypass timezoneOffset or fix (OBO) off by one day issues
        // Also fixes browser compatibility issues
        if (year && month) {
            response = new Date(month + '/01/' + year + ' 00:00:00');
        }
        if (year && !month) {
            response = new Date('01/01/' + year + ' 00:00:00');
        }
        return response;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UiDateFromPartService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UiDateFromPartService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UiDateFromPartService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [] });

class FaviconService {
    constructor() {
        this.FAVICON_ID = 'FAVICON_ID';
    }
    setFavicon(data) {
        this.removeExistingIcons();
        this.addFavicon(data);
    }
    addFavicon(config) {
        const { type, href } = config;
        const linkElement = document.createElement('link');
        linkElement.setAttribute('id', this.FAVICON_ID);
        linkElement.setAttribute('rel', 'icon');
        linkElement.setAttribute('type', type);
        linkElement.setAttribute('href', href);
        document.head.appendChild(linkElement);
    }
    removeExistingIcons() {
        const linkElements = document.querySelectorAll("link[ rel ~= 'icon' i]");
        for (const linkElement of Array.from(linkElements)) {
            linkElement?.parentNode?.removeChild(linkElement);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: FaviconService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: FaviconService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: FaviconService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [] });

// TODO: Move to utility library
function parseJSONIfParsable(str) {
    try {
        return JSON.parse(str);
    }
    catch (e) {
        return str;
    }
}
/**
 * To be used with an interface declared for the storage object
 * TODO: parse top-level date strings
 */
class BaseWebStorageService {
    setItem(key, value) {
        if (typeof value === 'object') {
            return this.storage.setItem(key, JSON.stringify(value));
        }
        return this.storage.setItem(key, value);
    }
    getItem(key) {
        const value = this.storage.getItem(key);
        if (isNil(value)) {
            return value;
        }
        const parsedValue = parseJSONIfParsable(value);
        if (typeof parsedValue === 'object') {
            return DateService.convertAllDateFields(parsedValue);
        }
        return parsedValue;
    }
    removeItem(key) {
        return this.storage.removeItem(key);
    }
    clear() {
        return this.storage.clear();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BaseWebStorageService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BaseWebStorageService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BaseWebStorageService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/**
 * To be used with an interface declared for local storage
 */
class BaseLocalStorageService extends BaseWebStorageService {
    constructor() {
        super(...arguments);
        this.storage = localStorage;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BaseLocalStorageService, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BaseLocalStorageService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BaseLocalStorageService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/**
 * @deprecated use the UiScrollLockingService in the ui-lib. Only use the service for scroll locking unrelated to ui-components - those should all implement their own scroll locking as an optional input
 */
class UiScrollLockingService {
    constructor() {
        this.scrollStatusSource = new BehaviorSubject(false);
        this.scrollStatusObservable$ = this.scrollStatusSource.asObservable();
    }
    scrollLock(status) {
        this.scrollStatusSource.next(status);
        const body = document.getElementsByTagName('body')[0];
        if (status) {
            body.classList.add('scroll-lock');
        }
        else {
            body.classList.remove('scroll-lock');
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UiScrollLockingService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UiScrollLockingService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UiScrollLockingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [] });

/**
 * To be used with an interface declared for local storage
 */
class BaseSessionStorageService extends BaseWebStorageService {
    constructor() {
        super(...arguments);
        this.storage = sessionStorage;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BaseSessionStorageService, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BaseSessionStorageService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BaseSessionStorageService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class VersionCheckService {
    constructor(http) {
        this.http = http;
        // this will be replaced by actual hash post-build.js
        this.currentHash = '';
        this.versionSubject$ = new BehaviorSubject(false);
    }
    watchVersion() {
        return this.versionSubject$.asObservable();
    }
    /**
     * Checks in every set frequency the version of frontend application
     * @param url
     * @param {number} frequency - in milliseconds, defaults to 15 minutes
     */
    initVersionCheck(environment, url, frequency = 1000 * 60 * 15) {
        this._environment = environment;
        this.currentHash = environment.version;
        setInterval(() => {
            this.checkVersion(url);
        }, frequency);
        this.checkVersion(url);
    }
    /**
     * Will do the call and check if the hash has changed or not
     * @param url
     */
    checkVersion(url) {
        // timestamp these requests to invalidate caches
        this.http.get(url + '?t=' + new Date().getTime()).subscribe((response) => {
            const hash = response[this._environment.env];
            const hashChanged = this.hasHashChanged(this.currentHash, hash);
            // If new version, do something
            if (hashChanged) {
                this.versionSubject$.next(true);
                // window.location.reload();
                // ENTER YOUR CODE TO DO SOMETHING UPON VERSION CHANGE
                // for an example: location.reload();
            }
            // store the new hash so we wouldn't trigger versionChange again
            // only necessary in case you did not force refresh
            this.currentHash = hash;
        }, (err) => {
            console.error(err, 'Could not get version');
        });
    }
    /**
     * Checks if hash has changed.
     * This file has the JS hash, if it is a different one than in the version.json
     * we are dealing with version change
     * @param currentHash
     * @param newHash
     * @returns {boolean}
     */
    hasHashChanged(currentHash, newHash) {
        if (!currentHash) {
            return false;
        }
        return currentHash !== newHash;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: VersionCheckService, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: VersionCheckService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: VersionCheckService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1.HttpClient }] });

// Tailwind v4 dimensions
const BREAKPOINTS = Object.freeze({ sm: 640, md: 768, lg: 1024, xl: 1280, '2xl': 1536 });
class ViewportService {
    constructor() {
        this.viewportSizeSource = new BehaviorSubject(undefined);
        /** Use {@link aboveBreakpoint} instead where possible */
        this.viewportSizeChanged$ = this.viewportSizeSource.asObservable();
        this.viewportWidthSource = new BehaviorSubject(0);
        this.viewportWidthObservable$ = this.viewportWidthSource.asObservable();
        this.viewPortSize();
        window.addEventListener('resize', (event) => {
            this.viewPortSize();
        });
    }
    getCurrentSize() {
        if (window.innerWidth < BREAKPOINTS.sm) {
            return 'xs';
        }
        else if (window.innerWidth < BREAKPOINTS.md) {
            return 'sm';
        }
        else if (window.innerWidth < BREAKPOINTS.lg) {
            return 'md';
        }
        else if (window.innerWidth < BREAKPOINTS.xl) {
            return 'lg';
        }
        else if (window.innerWidth < BREAKPOINTS['2xl']) {
            return 'xl';
        }
        else {
            return '2xl';
        }
    }
    /**
     * https://tailwindcss.com/docs/responsive-design#working-mobile-first
     * @example
     * Using this method means your tailwind breakpoints line up exactly with the observable
     * ```ts
     *  md$ = this.viewportService.aboveBreakpoint('md')
     *```
     * @param breakpoint
     * @returns true if screen width is higher OR EQUAL TO the specified breakpoint
     */
    aboveBreakpoint(breakpoint) {
        return this.viewportWidthObservable$.pipe(map((width) => width >= BREAKPOINTS[breakpoint]), distinctUntilChanged());
    }
    /**
     * To be most consistent with Tailwind, try to use {@link aboveBreakpoint} instead
     * Why? https://tailwindcss.com/docs/responsive-design#working-mobile-first
     * @param breakpoint
     * @returns true if the width is STRICTLY LOWER than the specified breakpoint
     */
    belowBreakpoint(breakpoint) {
        return this.viewportWidthObservable$.pipe(map((width) => width < BREAKPOINTS[breakpoint]), distinctUntilChanged());
    }
    viewPortSize() {
        this.viewportWidthSource.next(window.innerWidth);
        this.viewportSizeSource.next(this.getCurrentSize());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ViewportService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ViewportService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ViewportService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [] });

/**
 * Used in an ngFor to prevent the list we're iterating over from rerendering unless the ID changes
 */
function trackById(_index, item) {
    return item.id;
}

const DeclareExportItems = [
    DeepCopyPipe,
    LinkifyPipe,
    InitialsPipe,
    TruncatePipe,
    RangePipe,
    LimitPipe,
    InvokePipe,
    NumberComparePipe,
    DropdownCloserDirective,
    ClickOutsideDirective,
    EscapeKeyDirective,
    ResizableImageDirective,
];
class WebUtilityModule {
    static forRoot(config) {
        const defaults = {
            resizableImageConfig: {
                cdnUrl: '',
            },
        };
        return {
            ngModule: WebUtilityModule,
            providers: [
                {
                    provide: RESIZABLE_IMAGE_CONFIG,
                    useValue: config?.resizableImageConfig ?? defaults.resizableImageConfig,
                },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: WebUtilityModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: WebUtilityModule, imports: [DeepCopyPipe,
            LinkifyPipe,
            InitialsPipe,
            TruncatePipe,
            RangePipe,
            LimitPipe,
            InvokePipe,
            NumberComparePipe,
            DropdownCloserDirective,
            ClickOutsideDirective,
            EscapeKeyDirective,
            ResizableImageDirective, CommonModule], exports: [DeepCopyPipe,
            LinkifyPipe,
            InitialsPipe,
            TruncatePipe,
            RangePipe,
            LimitPipe,
            InvokePipe,
            NumberComparePipe,
            DropdownCloserDirective,
            ClickOutsideDirective,
            EscapeKeyDirective,
            ResizableImageDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: WebUtilityModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: WebUtilityModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...DeclareExportItems, CommonModule],
                    exports: [...DeclareExportItems],
                }]
        }] });

/*
 * Public API Surface of web-utilities
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ARRAY, BIT, BITS, BYTE, BYTES, BaseLocalStorageService, BaseSessionStorageService, BaseWebStorageService, ClickOutsideDirective, CookieService, DeepCopyPipe, DisplayPager, DropdownCloserDirective, E11RouteReuseStrategy, EMPTY, EXPONENT, EscapeKeyDirective, FUNCTION, FaviconService, FileMimeTypeIconTypes, FormSelectModel, IEC, INVALID_NUMBER, INVALID_ROUND, ImageTypeEnum, InitialsPipe, InvokePipe, JEDEC, LimitPipe, LinkifyPipe, NumberComparePipe, OBJECT, PERIOD, RESIZABLE_IMAGE_CONFIG, ROUND, RangePipe, ResizableImageDirective, S, SI_KBIT, SI_KBYTE, SPACE, STRING, STRINGS, States, Time, TruncatePipe, UiDateFromPartService, UiScrollLockingService, VersionCheckService, ViewportService, WebUtilityModule, ZERO, _fileSize, autoDestroy, copyToClipboard, dataURLtoFile, dataURLtoImage, destroyNotifier, dispatchIfNotLoaded, fileSize, fileToDataURL, filter, findFileIcon, formSelectOptionsBusinessSizeRange, formSelectOptionsBusinessType, formSelectOptionsCountry, formSelectOptionsFutureYears, formSelectOptionsGICSIndustryGroup, formSelectOptionsIndustrySectorList, formSelectOptionsMonths, formSelectOptionsPastYears, formSelectOptionsStates, formattedSize, getFileMimeType, getImageDimensions, hasChangedAnyNg, hasChangedNg, isMobile, isMobileOrTablet, mappingToFormSelect, mimeTypeIconMap, openInNewTab, removeDuplicatesByKeyIgnoreCase, removeDuplicatesByNameIgnoreCase, stateCodeToOption, textMatches, trackById, urlToBlob, validateImageDimensions };
